import { RouteModule } from '#/horizon/components/RouteModule';
import { setSurrogate } from '#/horizon/middlewares/surrogate.middleware';
import { getServerRouterContext } from '#/horizon/router-contexts/server-router-context';
import { useI18n } from '#/i18n/hooks/useI18n';
import type {
  LayoutHandleElements,
  LayoutHandleProps,
} from '#/layouts/Layout/LayoutHandleProps';
import { DynamicSearchEventsRefinements } from '#/modules/search/events/components/Refinements/DynamicSearchEventsRefinements';
import { searchEventsPathByLocale } from '#/modules/search/events/routing/searchEvents.paths';
import { searchEventsPathReference } from '#/modules/search/routing/search.paths';
import { getSearchPageLinkRels } from '#/modules/search/utils/getSearchPageLinkRels';
import type { RoutePathLoaderArgs } from '#/routing/classes/types/RoutePathLoaderArgs';
import type { RouteProps } from '#/routing/types/RouteProps';

import { SearchEventsPage } from './SearchEvents.page';

export const handle = {
  searchRefinements: <DynamicSearchEventsRefinements />,
} satisfies LayoutHandleElements;

export function loader({
  url,
  context,
}: RoutePathLoaderArgs<typeof searchEventsPathReference>) {
  setSurrogate({ context, data: { type: 'hardcoded-route' } });
  const { serverContext } = getServerRouterContext(context);

  return {
    handle: {
      urlByLocale: searchEventsPathByLocale,
      darkBackground: true,
      defaultSearchType: 'EVENT',
      hideFooterDivider: true,
      searchFromPageType: 'SEARCH',
      showDesktopSearch: true,
      showSearchRefinements: true,
      isSearchPage: true,
      hideMobileHeaderOnScroll: true,
    } satisfies LayoutHandleProps,

    linkRels: getSearchPageLinkRels({
      searchParams: url.searchParams,
      locale: serverContext.locale,
      pathByLocale: searchEventsPathByLocale,
      referenceRoute: searchEventsPathReference,
    }),
    serverNow: serverContext.now,
  };
}

export default function SearchEventsRoute({
  loaderData: { linkRels, serverNow },
}: RouteProps<typeof loader>) {
  const { i18n } = useI18n();

  return (
    <RouteModule
      title={
        /* TRANSLATORS: events search page title */
        i18n('Social-Impact & Nonprofit Events | Online & Local Opportunities')
      }
      description={
        /* TRANSLATORS: events search page title */
        i18n('Discover social-impact events near you or online')
      }
      linkRels={linkRels}
    >
      <SearchEventsPage serverNow={serverNow} />
    </RouteModule>
  );
}
